import { Text, TextField, Title2, WasabiButton } from '@kk/ui';
import { useEffect, useRef, useState } from 'react';

import { useClients, useSession } from '@/auth/SessionProvider';
import { LinkButton, Status } from '@/components/bits';
import { deck } from '@/copy/deck';
import { useErrorMessage } from '@/data/hooks';
import { SignInShell } from '@/screens/SignInShell';

const c = deck.o17;

/** Reloading rebuilds the session from /v1/org/me, which now carries the content role. Replaceable in tests. */
export const confirmTeamEmailEffects = { reload: () => window.location.reload() };

/**
 * **Confirm your email, to join the team** (Evan, 2026-09-26). A teammate who signs in to the admin with email and
 * password is never verified by Auth0 — its verification email is off — so an invite waiting on their address would
 * wait forever. This sends the app's own 6-digit code (P23c's `/v1/me/email/code`) as the screen opens, takes it, and
 * the confirmation joins the invite on the server; the reload then opens the admin.
 */
export function ConfirmTeamEmail() {
  const { data } = useClients();
  const { session } = useSession();
  const errorMessage = useErrorMessage();
  const [email, setEmail] = useState(session?.user.email ?? '');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const asked = useRef(false);

  const send = () => {
    setError(null);
    data
      .requestEmailCode()
      .then((r) => setEmail(r.email))
      .catch((e: unknown) => setError(errorMessage(e)));
  };
  // one code as the screen opens — StrictMode's second effect must not send a second one
  useEffect(() => {
    if (asked.current) return;
    asked.current = true;
    send();
  }, []);

  const confirm = () => {
    setError(null);
    setBusy(true);
    data
      .confirmEmailCode(code.trim())
      .then(() => confirmTeamEmailEffects.reload())
      .catch((e: unknown) => setError(errorMessage(e)))
      .finally(() => setBusy(false));
  };

  return (
    <SignInShell>
      <div className="stack" data-testid="confirm-team-email">
        <Title2 tone="ink">{c.confirmTitle}</Title2>
        <Text variant="bodySm" tone="slate" testID="confirm-team-email-body">
          {c.confirmBody(email)}
        </Text>
        {/* the heading names the field; a second visible label would say it twice */}
        <TextField
          accessibilityLabel={c.confirmTitle}
          value={code}
          onChangeText={setCode}
          keyboardType="number-pad"
          testID="confirm-code"
        />
        {/^\d{6}$/.test(code.trim()) ? (
          <WasabiButton label={c.confirm} onPress={confirm} disabled={busy} testID="confirm-submit" />
        ) : null}
        <LinkButton label={c.newCode} onPress={send} testID="confirm-new-code" />
        {error ? <Status testID="confirm-error">{error}</Status> : null}
      </div>
    </SignInShell>
  );
}
